/**
 * "Actualizar" y "Actualizado hace…" (QA-2 de la feature 005).
 *
 * Vuelve a pedir los datos de la pantalla con los filtros **aplicados** (refetch de TanStack
 * Query). En una lista paginada por cursor (`useInfiniteQuery`) se pasa `pagesKey`: antes del
 * refetch se recorta la caché a la primera página, así la lista vuelve al principio y no se piden
 * de nuevo todas las páginas que se habían cargado con "Cargar más".
 *
 * El icono gira solo mientras dura el refetch pedido con el botón (no con el sondeo del
 * dashboard), y no gira con `prefers-reduced-motion`. El texto relativo sale de `dataUpdatedAt` y
 * se recalcula cada 15 s; el botón mide 44 px (design-system §10).
 *
 * Accesibilidad: "Actualizado hace…" es texto visible **sin** `aria-live` — con el sondeo del
 * dashboard (T-dashboard) y el propio recálculo cada 15 s, un lector de pantalla lo anunciaría todo
 * el tiempo sin que el usuario hiciera nada. En su lugar, una región `aria-live="polite"` aparte
 * (oculta, `sr-only`) anuncia "Datos actualizados" solo cuando termina un refetch **pedido por el
 * usuario** con este botón.
 */
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

import { Button } from './button';

/** Cada cuánto se recalcula "Actualizado hace…". */
export const UPDATED_AGO_TICK_MS = 15_000;

/** Lo mínimo que se necesita de un resultado de `useQuery`/`useInfiniteQuery`. */
export interface RefreshableQuery {
  refetch: () => Promise<unknown>;
  /** `0` mientras nunca llegó una respuesta. */
  dataUpdatedAt: number;
}

interface InfiniteData {
  pages: unknown[];
  pageParams: unknown[];
}

function isInfiniteData(value: unknown): value is InfiniteData {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as InfiniteData).pages) &&
    Array.isArray((value as InfiniteData).pageParams)
  );
}

/** "Actualizado hace un momento" / "hace 30 s" / "hace 4 min" / "hace 2 h". */
export function formatUpdatedAgo(updatedAt: number, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - updatedAt) / 1000));
  if (seconds < 10) {
    return t('common.refresh.justNow');
  }
  if (seconds < 60) {
    return t('common.refresh.secondsAgo', { value: seconds });
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return t('common.refresh.minutesAgo', { value: minutes });
  }
  return t('common.refresh.hoursAgo', { value: Math.floor(minutes / 60) });
}

/** Texto "Actualizado hace…" que se recalcula solo cada `UPDATED_AGO_TICK_MS`. */
export function UpdatedAgo({ updatedAt, className }: { updatedAt: number; className?: string }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // El intervalo se reinicia con cada respuesta: el primer tick llega 15 s después de ella.
    const id = setInterval(() => setNow(Date.now()), UPDATED_AGO_TICK_MS);
    return () => clearInterval(id);
  }, [updatedAt]);

  if (updatedAt <= 0) {
    return null;
  }
  return (
    // Sin aria-live: se recalcula solo (cada 15 s y con cada sondeo del dashboard) y no debe
    // anunciarse cada vez — ver el anuncio aparte en `RefreshButton`.
    <span
      data-testid="updated-ago"
      className={cn('text-fm-xs text-fm-text-secondary tabular-nums', className)}
    >
      {/* Datos más nuevos que el último tick: "hace un momento" sin esperar al siguiente. */}
      {formatUpdatedAgo(updatedAt, Math.max(now, updatedAt))}
    </span>
  );
}

export interface RefreshButtonProps {
  query: RefreshableQuery;
  /**
   * Clave exacta de una `useInfiniteQuery`: se recorta a la primera página antes de refetchear.
   * Sin ella se hace un refetch simple (listas no paginadas, dashboard).
   */
  pagesKey?: QueryKey;
  className?: string;
}

export function RefreshButton({ query, pagesKey, className }: RefreshButtonProps) {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  // Anuncio para lectores de pantalla, aparte del texto visible de `UpdatedAgo`: se limpia al
  // empezar cada refetch pedido por el botón para que el siguiente aviso, aunque diga lo mismo,
  // siempre cuente como un cambio y se vuelva a anunciar.
  const [announcement, setAnnouncement] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function refresh() {
    if (refreshing) {
      return;
    }
    setRefreshing(true);
    setAnnouncement('');
    try {
      const data: unknown = pagesKey ? queryClient.getQueryData(pagesKey) : undefined;
      if (pagesKey && isInfiniteData(data) && data.pages.length > 1) {
        queryClient.setQueryData<InfiniteData>(pagesKey, {
          pages: data.pages.slice(0, 1),
          pageParams: data.pageParams.slice(0, 1),
        });
      }
      await query.refetch();
      if (mounted.current) {
        setAnnouncement(t('common.refresh.announced'));
      }
    } finally {
      if (mounted.current) {
        setRefreshing(false);
      }
    }
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-fm-2', className)}>
      <Button
        variant="secondary"
        size="md"
        data-testid="refresh-button"
        aria-busy={refreshing || undefined}
        icon={
          <RefreshCw
            size={20}
            className={cn(refreshing && 'animate-spin motion-reduce:animate-none')}
          />
        }
        onClick={() => void refresh()}
      >
        {t('common.buttons.refresh')}
      </Button>
      <UpdatedAgo updatedAt={query.dataUpdatedAt} />
      <span aria-live="polite" aria-atomic="true" data-testid="refresh-announcement" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
